/**
 * How wide the explorations sidebar is allowed to be, and what it is by default.
 *
 * The pane was one number, `SIDEBAR_W = 280`, and it is a number the reader can
 * drag now (D104): the rows print an exploration's seed on one line and cut it
 * with an ellipsis, and a column somebody can widen is the only honest answer
 * to "let me read the names". That turns one constant into the same three
 * questions `threadPane.ts` answers for the reading column — what the pane is
 * when nobody has said, what it may never be narrower than, and what it may
 * never be wider than on the window it finds itself in — and they live together
 * here for that module's reason: the third is asked again on every window
 * resize, and a stored width that pinned the field shut would otherwise be
 * unreachable from the surface that made it.
 *
 * Imports only the thread's floors, so the two panes and the field are held to
 * one arithmetic: the sidebar's ceiling is what is left after the thread's own
 * floor and the field's.
 */

import { THREAD_CANVAS_FLOOR, THREAD_MIN, THREAD_TAB_W } from './threadPane'

/**
 * The narrowest the pane may be, and also what it is until somebody drags it.
 *
 * 224, the owner's design for the pane (D353): the rows, the tools and the foot
 * are laid out at it, and the field gets back the 56px the old 280 held. A name
 * longer than the column still has the seam, which only grows the pane from
 * here — so the floor is the design's width and the reader's drag is the way
 * to the rest of a name, as it was at 280.
 */
export const SIDEBAR_MIN = 224

/** One press of ← or → on the separator: the thread's step, for the same
 *  reasons it has it. */
export const SIDEBAR_STEP = 24

/**
 * The sidebar's column, as a custom property on the document root.
 *
 * The thread's `THREAD_W_VAR` carries the whole argument for why a drag may not
 * go through the store: `rev` would restart the camera's flight on every
 * pointer sample. This one carries the INSET — 0 below the phone breakpoint,
 * the rail's width collapsed, the pane's width expanded — rather than only the
 * width, because two boxes read it: the pane's own, and the thread column's
 * `left`, which is docked against the pane's right edge and has to move with
 * it in lockstep while a drag is live, or the column would paint over the pane
 * being widened under it. App writes it from the committed inset, which is the
 * value of record; the seam writes it directly while a drag is live.
 */
export const SIDEBAR_INSET_VAR = '--sidebar-inset'

/**
 * The slide the pane and the column ride when the inset changes, as a custom
 * property so a live drag can take it away.
 *
 * Collapsing the pane to its rail eases everything beside it over 380ms, and
 * that easing is right for a press and wrong for a hand: a box that eased
 * toward the pointer would lag it by the whole duration. The seam sets this to
 * zero for the life of a drag and removes it after, so the same transition
 * serves the press and stays out of the way of the drag.
 */
export const SIDEBAR_SLIDE_VAR = '--sidebar-slide'

/**
 * What the thread keeps from the stage whatever the sidebar would like: its own
 * floor while it is open, its reopen tab while it is folded, and nothing on an
 * empty canvas, where it is not mounted at all.
 */
export function rightReserve(threadOpen: boolean, threadTab: boolean): number {
  return threadOpen ? THREAD_MIN : threadTab ? THREAD_TAB_W : 0
}

/**
 * What the pane may be on this window, with this much of it already spoken for
 * on the right, and this much of it needed by the names.
 *
 * Two ceilings, and the lower one wins. The field's, stated from its side as
 * the thread's is: whatever is left after the thread's reserve and the canvas
 * floor. And the names': `fit` is the width at which every name on screen
 * reads in full, measured by the pane off its own rows — the seam stops just
 * past the longest name, because a pane wider than its longest name is field
 * given up for nothing (D104, follow-up). Null before the pane has measured,
 * which reads as the field's ceiling alone. `max` is never allowed below
 * `min`, for `threadBounds`'s reason — on a narrow desktop the floors cannot
 * be paid at all, and a max under min would make every clamp answer outside
 * its own range. The pane takes its minimum there, which is the width it
 * always had — and so it does when every name already fits at that width.
 */
export function sidebarBounds(
  stageW: number,
  reservedRight: number,
  fit: number | null,
): { min: number; max: number } {
  const room = Math.round(stageW - reservedRight - THREAD_CANVAS_FLOOR)
  const wanted =
    typeof fit === 'number' && Number.isFinite(fit) ? Math.min(room, Math.round(fit)) : room
  return { min: SIDEBAR_MIN, max: Math.max(SIDEBAR_MIN, wanted) }
}

/**
 * The pane's width now: what was stored if anything was, the minimum if not,
 * held inside the bounds either way.
 *
 * CLAMPED ON EVERY READ rather than only when it is set, which is what makes a
 * stored width safe to keep — `threadWidthFrom` says why: a pane dragged wide
 * on a big monitor and reopened on a laptop would otherwise leave the field a
 * sliver with the seam that would fix it off the edge of the window, and the
 * reader's number is still there when they go back to the big screen.
 */
export function sidebarWidthFrom(
  stageW: number,
  reservedRight: number,
  stored: number | null | undefined,
  fit: number | null,
): number {
  const { min, max } = sidebarBounds(stageW, reservedRight, fit)
  const want = typeof stored === 'number' && Number.isFinite(stored) ? stored : SIDEBAR_MIN
  return Math.min(max, Math.max(min, Math.round(want)))
}

/** One step of the separator's keyboard, held inside the same bounds. */
export function stepSidebarWidth(
  current: number,
  by: number,
  stageW: number,
  reservedRight: number,
  fit: number | null,
): number {
  return sidebarWidthFrom(stageW, reservedRight, current + by, fit)
}
